import { getFirestore, doc, collection, query, where, getDoc, getDocs, onSnapshot } from "firebase/firestore"
import getCollectionPath from '@/services/firestore/getCollectionPath'
import { SCHEMA_VERSION, migrateStrategyDataIfNeeded } from '@/services/migration/legacyMigration'

const db = getFirestore()
const collectionName = 'strategies/'

function docToInstance(doc) {
  const data = doc.data()
  if (!data) return null
  // Un document encore au format historique est converti en tâche de fond ; le listener
  // se redéclenchera avec le document allégé une fois la migration commitée.
  migrateStrategyDataIfNeeded(doc.id, data).catch(error => console.error('Migration de la stratégie ' + doc.id + ' impossible', error))
  return new Strategy(doc.id, data.period, data.team)
}

/**
 * Variante des lectures ponctuelles : la migration est **attendue** avant de rendre la main,
 * pour que tout code lisant ensuite les domaines les trouve effectivement écrits.
 */
async function docsToInstances(snapshots) {
  const list = []
  const migrations = []
  snapshots.forEach(snapshot => {
    const data = snapshot.data()
    if (!data) return
    migrations.push(migrateStrategyDataIfNeeded(snapshot.id, data))
    list.push(new Strategy(snapshot.id, data.period, data.team))
  })
  await Promise.all(migrations)
  return list
}

/**
 * Document principal d'une stratégie : identifiants et métadonnées uniquement.
 * Les données métier vivent dans les entités `Strategy*` (voir `strategyDomains.js`),
 * chargées et sauvegardées indépendamment.
 */
export default class Strategy {
  constructor(id, period, team) {
    this.id = id
    this.period = period
    this.team = team
    this.schemaVersion = SCHEMA_VERSION
  }

  static makeStrategyId(periodId, teamId) {
    return `${periodId}_${teamId}`
  }

  static initOne(periodId, teamId){
    return new Strategy(Strategy.makeStrategyId(periodId, teamId), periodId, teamId)
  }

  toData() {
    return {
      period: this.period,
      team: this.team,
      schemaVersion: SCHEMA_VERSION,
    }
  }

  async addToBatch(batch) {
    if (!this.id) this.id = Strategy.makeStrategyId(this.period, this.team)
    batch.set(doc(db, await getCollectionPath(collectionName), this.id), this.toData(), { merge: true })
    return this
  }

  static async listenAll(callback) {
    return onSnapshot(collection(db, await getCollectionPath(collectionName)), snapshot => {
      const list = []
      snapshot.forEach(doc => {
        list.push(docToInstance(doc))
      })
      callback(list)
    })
  }

  static async listenById(id, callback) {
    return onSnapshot(doc(db, await getCollectionPath(collectionName), id), snapshot => {
      callback(docToInstance(snapshot))
    })
  }

  static async listenByPeriodeAndTeam(periodId, teamId, callback) {
    const id = Strategy.makeStrategyId(periodId, teamId);
    const ref = doc(db, await getCollectionPath('strategies/'), id);

    return onSnapshot(ref, (snap) => {
      if (!snap.exists()) return callback([])
      callback([docToInstance(snap)])
    })
  }

  static async getAll() {
    const response = await getDocs(collection(db, await getCollectionPath(collectionName)))
    return docsToInstances(response)
  }

  static async getByPeriod(periodId){
    const response = await getDocs(query(collection(db, await getCollectionPath(collectionName)), where("period", "==", periodId)))
    return docsToInstances(response)
  }

  static async getByPeriodAndTeam(periodId, teamId){
    const id = Strategy.makeStrategyId(periodId, teamId)
    const ref = doc(db, await getCollectionPath(collectionName), id)
    const snap = await getDoc(ref)
    if (!snap.exists()) return []
    const data = snap.data()
    await migrateStrategyDataIfNeeded(snap.id, data)
    return [new Strategy(snap.id, data.period, data.team)]
  }

}
